// Returning to the item that was clicked. A list that opens another screen
// marks the clicked item's id in the history entry it is leaving; when the
// user comes back to that entry (the browser's back, or any navigate(-1)),
// the list finds the item again, scrolls it to the middle of the screen,
// focuses it and highlights it for a moment. The window's pixel position is
// still restored by React Router's <ScrollRestoration />; this refines it,
// and still works when the list changed meanwhile (a sync added games).

/** The attribute that names an element as a return target: `data-return-focus="<id>"`. */
export const RETURN_FOCUS_ATTR = 'data-return-focus';
/** The class the item wears while highlighted (core-theme draws it). */
export const RETURN_FOCUS_CLASS = 'return-focus-flash';
/** How long the highlight lasts. */
export const RETURN_FOCUS_MS = 1200;

/** The attribute to spread on an item that can be returned to: `<tr {...returnTarget(id)}>`. */
export function returnTarget(id: string): { 'data-return-focus': string } {
  return { 'data-return-focus': id };
}

/** The key under which the clicked id is kept in a history entry's state. */
const KEY = 'returnFocus';

/** The entry's state with the clicked id added, keeping what was there. */
export function withReturnFocus(state: unknown, id: string): Record<string, unknown> {
  const base = state && typeof state === 'object' && !Array.isArray(state) ? (state as Record<string, unknown>) : {};
  return { ...base, [KEY]: id };
}

/** The clicked id kept in an entry's state, if any. */
export function returnFocusOf(state: unknown): string | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const id = (state as Record<string, unknown>)[KEY];
  return typeof id === 'string' && id ? id : undefined;
}

/** The selector that finds the element marked with this id. */
export function returnFocusSelector(id: string): string {
  return `[${RETURN_FOCUS_ATTR}="${id.replace(/["\\]/g, '\\$&')}"]`;
}

/**
 * Whether to return to an item now: only when the entry was reached by going
 * back or forward (a POP), its state names an item, the list has rendered
 * its real content, and this entry has not been handled yet.
 */
export function shouldReturn(args: { navigationType: string; state: unknown; ready: boolean; entryKey: string; handled: string | undefined }): string | undefined {
  if (args.navigationType !== 'POP' || !args.ready || args.handled === args.entryKey) return undefined;
  return returnFocusOf(args.state);
}
